// app/javascript/scenes_editor/hotspots_manager.js
import * as THREE from "three"

const DEFAULT_RADIUS = 1400
const DEFAULT_SEGMENTS = 256
const DEFAULT_FOV = 58
const DEFAULT_NEAR = 0.1
const DEFAULT_FAR = 5000

export function initViewer(controller) {
  const el = controller.viewerEl
  if (!el) return

  const computedPos = window.getComputedStyle(el).position
  if (computedPos === "static") el.style.position = "relative"
  el.style.overflow = "hidden"

  controller.scene = new THREE.Scene()
  controller.scene.background = new THREE.Color(0x000000)

  const width = Math.max(el.clientWidth || 1, 1)
  const height = Math.max(el.clientHeight || 1, 1)

  controller.camera = new THREE.PerspectiveCamera(
    DEFAULT_FOV,
    width / height,
    DEFAULT_NEAR,
    DEFAULT_FAR
  )
  controller.camera.position.set(0, 0, 0)

  controller.renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
  })
  controller.renderer.setPixelRatio(window.devicePixelRatio || 1)
  controller.renderer.setSize(width, height)
  controller.renderer.outputColorSpace = THREE.SRGBColorSpace

  el.innerHTML = ""
  el.appendChild(controller.renderer.domElement)

  controller.renderer.domElement.style.touchAction = "none"
  controller.renderer.domElement.style.userSelect = "none"
  controller.renderer.domElement.style.display = "block"

  controller.loader = new THREE.TextureLoader()
  controller.currentTex = null

  controller.LOOK_OFFSET_DEG = 90
  controller.CENTER_OFFSET_DEG = 0

  controller.lon = 0
  controller.lat = 0

  controller.uiLeft = false
  controller.uiRight = false
  controller.KEY_SPEED_LON = 60

  controller.raycaster = new THREE.Raycaster()
  controller.mouse = new THREE.Vector2()

  controller.R = DEFAULT_RADIUS
  controller.SEG = DEFAULT_SEGMENTS
  controller.THETA_LENGTH = Math.PI
  controller.THETA_START = -Math.PI / 2

  buildCylinder180(controller)
  computeLimitsDeg(controller)
  clampAngles(controller)
  updateCameraLook(controller)
}

function computeInitialHeight(controller) {
  const vFovRad = THREE.MathUtils.degToRad(controller.camera.fov)
  return 2 * controller.R * Math.tan(vFovRad / 2)
}

export function buildCylinder180(controller) {
  controller.H = computeInitialHeight(controller)

  const geometry = makeCylinderGeometry(controller, controller.H)
  const material = new THREE.MeshBasicMaterial({
    side: THREE.BackSide,
    color: 0xffffff,
  })

  if (controller.pano) {
    controller.scene.remove(controller.pano)
    controller.pano.geometry?.dispose?.()
    controller.pano.material?.dispose?.()
  }

  controller.geometry = geometry
  controller.material = material
  controller.pano = new THREE.Mesh(geometry, material)
  controller.scene.add(controller.pano)
}

function makeCylinderGeometry(controller, height) {
  return new THREE.CylinderGeometry(
    controller.R,
    controller.R,
    height,
    controller.SEG,
    1,
    true,
    controller.THETA_START,
    controller.THETA_LENGTH
  )
}

export function computeLimitsDeg(controller) {
  const aspect = Math.max(controller.camera.aspect || 1, 0.0001)
  const vFov = THREE.MathUtils.degToRad(controller.camera.fov)
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect)

  const halfArc = controller.THETA_LENGTH / 2
  const halfView = hFov / 2

  const marginLon = THREE.MathUtils.degToRad(0.5)
  const limitLonRad = Math.max(0, halfArc - halfView - marginLon)

  controller.lonLimitDeg = THREE.MathUtils.radToDeg(limitLonRad)

  // piccolo margine verticale, come nel tuo file originale
  controller.latLimitDeg = 3
}

export function clampAngles(controller) {
  controller.lon = THREE.MathUtils.clamp(
    controller.lon,
    -controller.lonLimitDeg,
    controller.lonLimitDeg
  )

  controller.lat = THREE.MathUtils.clamp(
    controller.lat,
    -controller.latLimitDeg,
    controller.latLimitDeg
  )
}

function rebuildCylinderHeight(controller, nextHeight) {
  if (!nextHeight || nextHeight <= 0) return

  if (Math.abs(nextHeight - controller.H) <= 0.5) return

  controller.H = nextHeight

  const newGeometry = makeCylinderGeometry(controller, controller.H)

  if (controller.geometry) controller.geometry.dispose()
  controller.geometry = newGeometry

  if (controller.pano) controller.pano.geometry = newGeometry

  computeLimitsDeg(controller)
  clampAngles(controller)
  updateCameraLook(controller)
  updateHotspotPositions(controller)
}

function fitCylinderToTexture(controller, tex) {
  const img = tex?.image
  const w = img?.naturalWidth || img?.videoWidth || img?.width || 0
  const h = img?.naturalHeight || img?.videoHeight || img?.height || 0

  if (!w || !h) return

  const imageAspect = w / h
  const arcLength = controller.R * controller.THETA_LENGTH

  let idealH = arcLength / imageAspect

  const vFovRad = THREE.MathUtils.degToRad(controller.camera.fov)
  const visibleHeight = 2 * controller.R * Math.tan(vFovRad / 2)

  const minH = visibleHeight * 0.85
  const maxH = visibleHeight * 1.6

  idealH = THREE.MathUtils.clamp(idealH, minH, maxH)

  rebuildCylinderHeight(controller, idealH)
}

export function applyTexture(controller, tex) {
  if (!tex) return

  if (controller.currentTex) controller.currentTex.dispose()
  controller.currentTex = tex

  tex.colorSpace = THREE.SRGBColorSpace
  tex.flipY = true
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.generateMipmaps = true
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.magFilter = THREE.LinearFilter

  const maxAniso = controller.renderer?.capabilities?.getMaxAnisotropy?.() || 1
  tex.anisotropy = Math.min(8, maxAniso)

  // specchiatura orizzontale per visualizzazione interna corretta
  tex.repeat.x = -1
  tex.offset.x = 1

  fitCylinderToTexture(controller, tex)

  controller.material.map = tex
  controller.material.needsUpdate = true

  computeLimitsDeg(controller)
  clampAngles(controller)
  updateCameraLook(controller)
  updateHotspotPositions(controller)
}

export function loadTextureFromUrl(controller, url) {
  if (!url) return

  console.log("[EDITOR] loading texture:", url)

  controller.loader.load(
    url,
    (tex) => {
      console.log(
        "[EDITOR] texture loaded:",
        tex?.image?.width || tex?.image?.naturalWidth,
        tex?.image?.height || tex?.image?.naturalHeight
      )
      applyTexture(controller, tex)
      setCenteredView(controller)
    },
    undefined,
    (err) => {
      console.error("[EDITOR] texture load error:", url, err)
    }
  )
}

export function setCenteredView(controller) {
  controller.lon = 0
  controller.lat = 0
  clampAngles(controller)
  updateCameraLook(controller)
  controller.updateButtons?.()
  updateHotspotPositions(controller)
}

export function updateCameraLook(controller) {
  const theta = THREE.MathUtils.degToRad(
    controller.lon + controller.LOOK_OFFSET_DEG + controller.CENTER_OFFSET_DEG
  )
  const phi = THREE.MathUtils.degToRad(90 - controller.lat)

  const target = new THREE.Vector3(
    controller.R * Math.sin(phi) * Math.cos(theta),
    controller.R * Math.cos(phi),
    controller.R * Math.sin(phi) * Math.sin(theta)
  )

  controller.camera.lookAt(target)
}

export function lonLatFromScreen(controller, clientX, clientY) {
  const rect = controller.viewerEl?.getBoundingClientRect?.()
  if (!rect || !rect.width || !rect.height) return null

  const x = ((clientX - rect.left) / rect.width) * 2 - 1
  const y = -((clientY - rect.top) / rect.height) * 2 + 1

  controller.mouse.set(x, y)
  controller.raycaster.setFromCamera(controller.mouse, controller.camera)

  const hits = controller.raycaster.intersectObject(controller.pano)
  if (!hits.length) return null

  const p = hits[0].point

  const theta = Math.atan2(p.z, p.x)
  let lonDeg =
    THREE.MathUtils.radToDeg(theta) -
    controller.LOOK_OFFSET_DEG -
    controller.CENTER_OFFSET_DEG

  lonDeg = ((lonDeg + 180) % 360 + 360) % 360 - 180

  const latRad = Math.asin(
    THREE.MathUtils.clamp(p.y / controller.R, -1, 1)
  )
  const latDeg = THREE.MathUtils.radToDeg(latRad)

  return { lon: lonDeg, lat: latDeg }
}

export function renderHotspots(controller) {
  if (!controller.hasLayerTarget) return

  controller.layerTarget.innerHTML = ""
  controller.hotspotEls.clear()

  controller.hotspots.forEach((hs) => {
    const dot = document.createElement("div")
    dot.className = hs.css_classes?.trim() || "hs-dot"
    dot.dataset.hotspotId = String(hs.id)
    dot.style.position = "absolute"
    dot.style.transform = "translate(-50%, -50%)"

    dot.addEventListener("click", (e) => {
      e.stopPropagation()
      controller.selectHotspot(hs.id)
    })

    dot.addEventListener("pointerdown", (e) => {
      if (controller.editingId !== hs.id) return
      if (e.target.closest(".hs-dot__tools")) return
      if (e.target !== dot) return

      e.preventDefault()
      e.stopPropagation()

      controller.dragging = true
      controller._dragPointerId = e.pointerId
      controller._dragFromToolbar = false

      dot.setPointerCapture?.(e.pointerId)
      controller.onPointerMove?.(e)
    })

    controller.layerTarget.appendChild(dot)
    controller.hotspotEls.set(hs.id, dot)
  })

  updateHotspotPositions(controller)
}

export function updateHotspotPositions(controller) {
  if (!controller.hotspotEls?.size) return

  const rect = controller.viewerEl?.getBoundingClientRect?.()
  if (!rect || !rect.width || !rect.height) return

  const w = rect.width
  const h = rect.height

  for (const hs of controller.hotspots) {
    const el = controller.hotspotEls.get(hs.id)
    if (!el) continue

    const theta = THREE.MathUtils.degToRad(
      (hs.lon || 0) + controller.LOOK_OFFSET_DEG + controller.CENTER_OFFSET_DEG
    )
    const phi = THREE.MathUtils.degToRad(90 - (hs.lat || 0))

    const worldPoint = new THREE.Vector3(
      controller.R * Math.sin(phi) * Math.cos(theta),
      controller.R * Math.cos(phi),
      controller.R * Math.sin(phi) * Math.sin(theta)
    )

    const projected = worldPoint.clone().project(controller.camera)

    const isOutsideClip =
      projected.z < -1 ||
      projected.z > 1 ||
      projected.x < -1.15 ||
      projected.x > 1.15 ||
      projected.y < -1.15 ||
      projected.y > 1.15

    if (isOutsideClip) {
      el.style.display = "none"
      continue
    }

    const x = (projected.x * 0.5 + 0.5) * w
    const y = (-projected.y * 0.5 + 0.5) * h

    el.style.display = "block"
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }
}

export function startLoop(controller) {
  controller.lastTime = performance.now()

  const tick = (time) => {
    controller.raf = requestAnimationFrame(tick)

    const dt = Math.max(0, (time - controller.lastTime) / 1000)
    controller.lastTime = time

    if (!controller.dragging) {
      if (controller.uiLeft) controller.lon -= controller.KEY_SPEED_LON * dt
      if (controller.uiRight) controller.lon += controller.KEY_SPEED_LON * dt
    }

    clampAngles(controller)
    updateCameraLook(controller)
    controller.renderer.render(controller.scene, controller.camera)

    controller.updateButtons?.()
    updateHotspotPositions(controller)
  }

  controller.raf = requestAnimationFrame(tick)
}

export function stopLoop(controller) {
  if (controller.raf) cancelAnimationFrame(controller.raf)
  controller.raf = null
}

export function bindResize(controller) {
  controller._onResize = () => {
    const el = controller.viewerEl
    if (!el || !controller.camera || !controller.renderer) return

    const width = Math.max(el.clientWidth || 1, 1)
    const height = Math.max(el.clientHeight || 1, 1)

    controller.camera.aspect = width / height
    controller.camera.updateProjectionMatrix()

    controller.renderer.setSize(width, height)

    if (controller.currentTex) {
      fitCylinderToTexture(controller, controller.currentTex)
    }

    computeLimitsDeg(controller)
    clampAngles(controller)
    updateCameraLook(controller)
    updateHotspotPositions(controller)
  }

  window.addEventListener("resize", controller._onResize)
}

export function unbindResize(controller) {
  if (controller._onResize) {
    window.removeEventListener("resize", controller._onResize)
  }
  controller._onResize = null
}

export function disposeAll(controller) {
  try {
    if (controller.currentTex) {
      controller.currentTex.dispose()
      controller.currentTex = null
    }

    if (controller.pano) {
      controller.scene?.remove?.(controller.pano)
      controller.pano = null
    }

    if (controller.geometry) {
      controller.geometry.dispose()
      controller.geometry = null
    }

    if (controller.material) {
      controller.material.dispose()
      controller.material = null
    }

    if (controller.renderer) {
      controller.renderer.dispose()
      controller.renderer.forceContextLoss?.()
      controller.renderer.domElement?.remove?.()
      controller.renderer = null
    }
  } catch (_) {}

  if (controller.viewerEl) controller.viewerEl.innerHTML = ""
}